import {
  HomeSection,
  HomeSectionHorizontalScroll,
  HomeSectionHorizontalScrollContent,
  HomeSectionSubtitle,
  HomeSectionTitle,
} from '#/components/home-content/home-section';
import {TransactionTileSmall, TransactionTileSmallLoading} from '#/components/transaction/transaction-tile';
import {UncachedTransactionsContext} from '#/contexts/blockchain-transactions-provider';
import {useInjectedHomeSections} from '#/hooks/query/use-injected-home-sections';
import {useLands} from '#/hooks/query/use-lands';
import {useLatestApiTransactions} from '#/hooks/query/use-latest-transactions';
import {useQueryNavigationActions} from '#/hooks/use-query-navigation-actions';
import {InformationTab} from '#/navigation/tabs';
import {FunctionComponent, useContext} from 'react';
import {useTranslation} from 'react-i18next';
import {DisplayableShareLandTransaction} from 'shareland-common';

const numberOfTransactionsToDisplay = 15;

export const InjectedHomeLiveActivity: FunctionComponent = () => {
  const {data: {activity} = {}} = useInjectedHomeSections();

  if (!activity || !activity.active) {
    return null;
  }

  return <HomeLiveActivity />;
};

const HomeLiveActivity: FunctionComponent = () => {
  const {t} = useTranslation();

  const {getTransactions} = useContext(UncachedTransactionsContext);

  const blockchainTransactions = getTransactions();
  const {data: latestApiTransactions, isLoading: latestApiTransactionsIsLoading} = useLatestApiTransactions({
    take: numberOfTransactionsToDisplay,
  });

  const allTransactions = blockchainTransactions
    .concat(latestApiTransactions?.pages[0].results ?? [])
    .slice(0, numberOfTransactionsToDisplay);

  return (
    <HomeSection className='@container/transaction-section'>
      <HomeSectionTitle>{t('pages.home.liveActivity.title')}</HomeSectionTitle>
      <HomeSectionSubtitle>{t('pages.home.liveActivity.subtitle')}</HomeSectionSubtitle>
      <HomeSectionHorizontalScroll className='snap-x snap-mandatory @xl/transaction-section:snap-none'>
        <HomeSectionHorizontalScrollContent className='grid grid-rows-5 auto-cols-[93%] @xl/transaction-section:grid-rows-1 grid-flow-col-dense @xl/transaction-section:auto-cols-min gap-x-5 gap-y-2 relative'>
          <TransactionsContent transactions={allTransactions} isLoading={latestApiTransactionsIsLoading} />
        </HomeSectionHorizontalScrollContent>
      </HomeSectionHorizontalScroll>
    </HomeSection>
  );
};

const TransactionsContent: FunctionComponent<{transactions: DisplayableShareLandTransaction[]; isLoading: boolean}> = ({
  transactions,
  isLoading,
}) => {
  const {
    actionMenu: {openInformation},
  } = useQueryNavigationActions();

  const {data: lands} = useLands();

  if (isLoading) {
    return (
      <>
        {Array(15)
          .fill(0)
          .map((_, index) => (
            <TransactionTileSmallLoading key={index} index={index} />
          ))}
      </>
    );
  }

  return (
    <>
      {transactions.map((transaction, index) => {
        const land = lands?.find(land => land.id === transaction.landId);

        return (
          <div
            key={index}
            className='hover:grayscale cursor-pointer snap-center @xl/transaction-section:snap-none'
            onClick={() => {
              openInformation(land, InformationTab.ACTIVITY);
            }}
          >
            <TransactionTileSmall transaction={transaction} />
          </div>
        );
      })}
      <div className='w-0 h-4 px-1'></div>
    </>
  );
};
